/**
 * Sub-aba "Vincular Produto E-mail": um arquivo de e-mail de referência por produto, guardado no
 * bucket `materiais`. Porte de `admin.html:2045-2074` (tela) e `9195-9291` (comportamento); linhas
 * 136 a 138 do inventário.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO: este envio não tem o limite de 10 MB que os outros
 * uploads do admin têm (o legado não confere o tamanho aqui), e a remoção apaga o arquivo do bucket
 * sem olhar se deu certo, antes de apagar a linha. Migração reproduz; a correção é do fundador.
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma.
import { Botao, Selecao } from '@complexity/ui';
// O nome aceito pelo Storage e a pasta administrativa, com as regras do legado.
import { CAMINHO_ARQUIVOS_ADMIN, sanitizarNomeArquivo, type LinhaParametrosProdutoEmail } from '@studiolu/dados';
// As chamadas da área.
import {
  enviarArquivoAoBucket,
  inserirEmailDeProduto,
  linkAssinado,
  listarEmailsDeProduto,
  removerArquivoDoBucket,
  removerEmailDeProduto,
} from '@studiolu/dados/areas/sistema';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área, e o status que some em 3 s (`mostrarStatusParamEmail`, `admin.html:9199`).
import { gravarNaMemoria, lerDaMemoria, useMemoria, useStatusDaArea } from './memoria.js';
// A linha compacta dos campos curtos.
import { LinhaCompacta } from '../financeiro/apoio.js';
// Os produtos na ordem do legado.
import { PARAM_PRODUTOS_LABEL, opcoesDe } from './catalogos.js';
// As peças visuais da área.
import {
  BotaoDaLinha,
  CabecalhoDeColunas,
  CabecalhoDeProduto,
  CampoArquivo,
  CartaoParam,
  LinhaParam,
  NotaSecao,
  TextoParam,
  type Carga,
  type PropsDaSubAba,
} from './pecas.js';

// As opções do "Produto", na ordem do HTML do legado (`admin.html:2052-2060`).
const OPCOES_DE_PRODUTO = opcoesDe(PARAM_PRODUTOS_LABEL);
// Onde o arquivo escolhido espera, na memória da área (o `#param-email-novo-arquivo` do legado).
const CHAVE_DO_ARQUIVO = 'sistema:email:arquivo';
// A ordem do catálogo, que decide a ordem dos cartões.
const ORDEM_DO_CATALOGO = Object.keys(PARAM_PRODUTOS_LABEL);

// Uma linha da lista, com o link assinado já pronto (ou `#`, quando a assinatura falhou).
interface EmailComLink {
  readonly linha: LinhaParametrosProdutoEmail;
  readonly link: string;
}

export function SubAbaEmailProduto({ confirmar, alertar, recarga }: PropsDaSubAba) {
  const { dados } = usePainel();
  const [carga, setCarga] = useState<Carga<readonly EmailComLink[]>>({ tipo: 'carregando' });
  // As linhas em memória (`parametrosEmailCarregados`).
  const emails = useRef<readonly LinhaParametrosProdutoEmail[]>([]);
  // O formulário, na memória da área: no legado o `<select>` e o campo de arquivo são fixos da
  // página (`admin.html:2050-2066`), e ir a outra aba e voltar encontra o produto e o arquivo
  // escolhidos. O arquivo mora na chave abaixo, que o `CampoArquivo` lê e escreve.
  const [produto, setProduto] = useMemoria('sistema:email:produto', '');
  // O parágrafo `#param-email-status`, fora da lista no legado (`admin.html:2071`).
  const [status, mostrarStatus] = useStatusDaArea('sistema:email:status');

  /**
   * Desenha a lista: ordena pelo catálogo e assina cada arquivo por uma hora, UM DE CADA VEZ, como
   * o `for ... await` do legado (`renderizarParametrosEmail`, `admin.html:9217-9253`). Enquanto
   * assina, a tela continua mostrando o que já mostrava.
   */
  const desenhar = useCallback(async () => {
    if (emails.current.length === 0) {
      setCarga({ tipo: 'pronta', dados: [] });
      return;
    }
    // Produto fora do catálogo vai para o fim, como o `999` do legado.
    const posicao = (p: string) => (ORDEM_DO_CATALOGO.indexOf(p) === -1 ? 999 : ORDEM_DO_CATALOGO.indexOf(p));
    const ordenados = [...emails.current].sort((a, b) => posicao(a.produto) - posicao(b.produto));
    const comLink: EmailComLink[] = [];
    for (const linha of ordenados) {
      // Sem caminho não há o que assinar; a falha de assinatura vira `#`, como no legado.
      const assinado = linha.arquivo_path ? await linkAssinado(dados, linha.arquivo_path) : null;
      comLink.push({ linha, link: (assinado?.ok ? assinado.dados : null) || '#' });
    }
    setCarga({ tipo: 'pronta', dados: comLink });
  }, [dados]);

  // `carregarParametrosEmail` (`admin.html:9205-9215`).
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    const resultado = await listarEmailsDeProduto(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    emails.current = resultado.dados ?? [];
    await desenhar();
  }, [dados, desenhar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // "Remover" (`admin.html:9255-9267`).
  const remover = useCallback(
    async (id: number) => {
      if (!(await confirmar('Remover este vínculo?'))) return;
      const linha = emails.current.find((r) => r.id === id);
      // O arquivo sai do bucket primeiro, e o resultado não é conferido, como no legado.
      if (linha?.arquivo_path) await removerArquivoDoBucket(dados, linha.arquivo_path);
      const resultado = await removerEmailDeProduto(dados, id);
      if (!resultado.ok) {
        await alertar('Erro ao remover: ' + resultado.erro.mensagem);
        return;
      }
      emails.current = emails.current.filter((r) => r.id !== id);
      // O legado redesenha sem esperar (`renderizarParametrosEmail()` sem `await`) e já avisa.
      void desenhar();
      mostrarStatus('Vínculo removido.');
    },
    [dados, confirmar, alertar, desenhar, mostrarStatus],
  );

  // "Adicionar" (`admin.html:9270-9291`).
  const adicionar = useCallback(async () => {
    // O arquivo vem da memória da área, onde o `CampoArquivo` o guardou: é o `input.files[0]` do
    // legado, que continua valendo depois de a pessoa ir a outra aba e voltar.
    const arquivo = lerDaMemoria<File | null>(CHAVE_DO_ARQUIVO, null);
    if (!produto || !arquivo) {
      mostrarStatus('Selecione produto e arquivo.');
      return;
    }
    if (emails.current.some((r) => r.produto === produto)) {
      mostrarStatus('Este produto já possui um e-mail vinculado. Remova antes de adicionar outro.');
      return;
    }
    // O caminho do legado: pasta administrativa, `email-referencia`, o produto e o nome limpo.
    const caminho = `${CAMINHO_ARQUIVOS_ADMIN}/email-referencia/${produto}/${sanitizarNomeArquivo(arquivo.name)}`;
    // Com `upsert`: enviar de novo o mesmo nome substitui (`admin.html:9279`).
    const envio = await enviarArquivoAoBucket(dados, caminho, arquivo, true);
    if (!envio.ok) {
      await alertar('Erro ao enviar arquivo: ' + envio.erro.mensagem);
      return;
    }
    // O nome guardado é o ORIGINAL, com acento; só o caminho é limpo.
    const resultado = await inserirEmailDeProduto(dados, produto, caminho, arquivo.name);
    if (!resultado.ok) {
      await alertar('Erro ao adicionar: ' + resultado.erro.mensagem);
      return;
    }
    emails.current = [...emails.current, resultado.dados];
    await desenhar();
    // Limpa o produto e o arquivo escolhido (o campo acompanha a memória e esvazia junto).
    setProduto('');
    gravarNaMemoria<File | null>(CHAVE_DO_ARQUIVO, null);
    mostrarStatus('Vínculo adicionado.');
  }, [produto, dados, alertar, desenhar, mostrarStatus, setProduto]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Vincula um arquivo de e-mail de referência a cada produto. Cada produto pode ter apenas 1 arquivo vinculado.
      </NotaSecao>

      <LinhaCompacta>
        <Selecao
          rotulo="Produto"
          vazio="(selecione)"
          opcoes={OPCOES_DE_PRODUTO}
          value={produto}
          onChange={(evento) => setProduto(evento.target.value)}
        />
        <CampoArquivo chave={CHAVE_DO_ARQUIVO} rotulo="Arquivo de e-mail" />
        <Botao onClick={() => void adicionar()}>Adicionar</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-email" />

      {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
      {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
      {carga.tipo === 'pronta' && carga.dados.length === 0 && <TextoParam>Nenhum vínculo cadastrado.</TextoParam>}
      {carga.tipo === 'pronta' && carga.dados.length > 0 && (
        <div className="flex flex-col gap-cx-4" data-testid="lista-emails">
          {carga.dados.map(({ linha, link }) => (
            <CartaoParam key={linha.id}>
              {/* Um cartão por produto, sempre "1 arquivo" (`admin.html:9238-9241`). */}
              <CabecalhoDeProduto titulo={PARAM_PRODUTOS_LABEL[linha.produto] || linha.produto} selo="1 arquivo" />
              <CabecalhoDeColunas>
                <span className="min-w-0 flex-1">Arquivo</span>
                <span className="w-[76px] shrink-0" />
              </CabecalhoDeColunas>
              <LinhaParam data-id={String(linha.id)}>
                <span className="min-w-0 basis-full text-cx-sm [overflow-wrap:anywhere] md:flex-1 md:basis-0">
                  {/* O nome do arquivo é o link assinado, em nova aba. */}
                  <a href={link} target="_blank" rel="noopener noreferrer" className="text-marca underline underline-offset-4">
                    {linha.arquivo_nome}
                  </a>
                </span>
                <span className="ml-auto shrink-0 text-right md:w-[76px]">
                  <BotaoDaLinha perigo onClick={() => void remover(linha.id)}>Remover</BotaoDaLinha>
                </span>
              </LinhaParam>
            </CartaoParam>
          ))}
        </div>
      )}
    </div>
  );
}
